import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import type { ContentFeedParticipant } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { getElapsed } from '@sola/utils/relativeTime.ts';
import type { ContentFeedCardProps, ContentFeedMatch, ContentFeedMedia } from './content-feed-card.tsx';
type SourceType = ContentFeedCardProps['sourceType'];

/**
 * Sponsored children connected to an entry.
 *
 * `names` may be shorter than `count` (or empty) when the supporter's sponsorship
 * data has not resolved yet. `count` is always the number of connected children,
 * so count-based copy stays available even when no names can be resolved.
 */
export interface RelatedChildren {
  names: string[];
  count: number;
}

/** An FCP ID is the first 6 characters of a participant localId (DR054002196 → DR0540). */
const fcpIdLength = 6;
const noRelatedChildren: RelatedChildren = {
  names: [],
  count: 0
};
const emptyMedia: ContentFeedMedia[] = [];

/**
 * Fallback dimensions for the Lightbox Image schema which requires numeric width/height.
 * These represent a standard 3:2 landscape ratio matching typical content feed imagery.
 * TODO: Remove once the upstream API guarantees dimensions on all media items.
 */
const fallbackImageWidth = 700;
const fallbackImageHeight = 467;

/**
 * Resolves the sponsored children connected to an entry, and their names when available.
 *
 * Participant-tier entries carry their own `participants` array, which identifies exactly
 * which children are featured. Otherwise the match context is used: a participant-filtered
 * query returns `related.participants` directly, while an FCP-filtered query returns
 * `related.fcps`, which is reverse-looked-up against the supporter's sponsored children
 * (a child's localId is prefixed with its FCP ID).
 */
export const resolveRelatedChildren = (match?: ContentFeedMatch, entryParticipants?: ContentFeedParticipant[], participantNameMap?: Map<string, string>): RelatedChildren => {
  const namesOf = (localIds: string[]): string[] => participantNameMap ? localIds.map(localId => participantNameMap.get(localId)).filter((name): name is string => !!name) : [];

  // Participant tier: the entry itself names the children it features.
  // Filter to only the supporter's sponsored children (those present in the name map).
  if (match?.tier === 'participant' && entryParticipants?.length) {
    const sponsoredLocalIds = participantNameMap ? entryParticipants.map(p => p.localId).filter(localId => participantNameMap.has(localId)) : entryParticipants.map(p => p.localId);
    return {
      names: namesOf(sponsoredLocalIds),
      count: sponsoredLocalIds.length
    };
  }

  // Participant-filtered query: related participants give both names and count
  const relatedParticipants = match?.related.participants;
  if (relatedParticipants?.length) {
    const localIds = relatedParticipants.map(p => p.localId);
    return {
      names: namesOf(localIds),
      count: localIds.length
    };
  }

  // FCP-filtered query: find sponsored children whose localId sits under a matched FCP.
  // This path depends on the name map, so no count is derivable without it.
  const relatedFcps = match?.related.fcps;
  if (relatedFcps?.length && participantNameMap) {
    const fcpIds = new Set(relatedFcps.map(f => f.id));
    const names: string[] = [];
    for (const [localId, name] of participantNameMap) {
      if (fcpIds.has(localId.slice(0, fcpIdLength))) {
        names.push(name);
      }
    }
    return {
      names,
      count: names.length
    };
  }
  return noRelatedChildren;
};

/** Structured banner data for IntlBlock rendering with rich text support. */
export interface BannerI18n {
  i18nKey: string;
  values?: Record<string, string | number>;
  /**
   * Resolved child names to reveal in a hover/tap tooltip on the banner text.
   * Only populated for the count-based ("_other") variant, where the visible
   * copy shows a count rather than names. Holds every name we could resolve,
   * even when fewer than `count` (sponsorship data may be incomplete).
   */
  tooltipNames?: string[];
}

/**
 * Derive banner i18n data from the query match context.
 * Produces a relevance label based on the match tier, related entities, and participant names.
 *
 * Uses i18next plural suffix convention (_one, _two, _other) with a single key
 * per tier. Returns structured data for IntlBlock rendering so HTML in locale
 * values (e.g. <strong>) renders correctly.
 */
export const deriveBannerText = (match: ContentFeedMatch | undefined, related: RelatedChildren, programCountryName?: string, sourceType?: SourceType): BannerI18n => {
  let fallback: BannerI18n = {
    i18nKey: 'my:contentFeed.bannerDefault'
  };
  let localeKey: string | undefined = undefined;
  let country: string | undefined = undefined;
  switch (match?.tier) {
    case 'participant':
      {
        localeKey = 'my:contentFeed.bannerParticipant';
        break;
      }
    case 'fcp':
      {
        // Community posts come from the center itself, so they use attendance copy.
        // Other sources (compassion.media, causes) describe the surrounding community.
        localeKey = sourceType === 'community' ? 'my:contentFeed.bannerCommunity' : 'my:contentFeed.bannerFcp';
        break;
      }
    case 'cluster':
      {
        localeKey = 'my:contentFeed.bannerCluster';
        break;
      }
    case 'country':
      {
        // Country copy interpolates the country name, so fall back to the regional
        // phrasing when the name is unavailable or no children resolved.
        if (!programCountryName) {
          return {
            i18nKey: 'my:contentFeed.bannerCountryRegion'
          };
        }
        localeKey = 'my:contentFeed.bannerCountry';
        country = programCountryName;
        // Country always uses the regional fallback when children don't resolve,
        // rather than the generic "From Compassion" default.
        fallback = {
          i18nKey: 'my:contentFeed.bannerCountryRegion'
        };
        break;
      }
  }
  if (!related.count || related.count === 1 && related.names?.length !== 1 || !localeKey) {
    return fallback;
  }
  const isOneName = related.count === 1;
  const isTwoNames = related.count === 2 && related.names.length === 2;
  // Everything else renders the count-based ("_other") copy. There the visible
  // text shows a number rather than names, so surface the resolved names
  // (however many we have) in a tooltip.
  const isCountOnly = !isOneName && !isTwoNames;
  return {
    i18nKey: localeKey,
    values: {
      count: related.count,
      ...(isOneName && {
        name1: related.names[0]
      }),
      ...(isTwoNames && {
        context: 'two',
        name1: related.names[0],
        name2: related.names[1]
      }),
      ...(country && {
        country
      })
    },
    ...(isCountOnly && related.names.length > 0 && {
      tooltipNames: related.names
    })
  };
};

/**
 * Derive the banner icon so it matches the banner text.
 *
 * Icon mapping:
 * - participant tier, one child → 'child'
 * - participant tier, multiple children → 'children'
 * - fcp tier, community source → 'fcp'
 * - fcp tier, other sources → 'marker-pin-04'
 * - cluster tier → 'signpost'
 * - country tier → 'globe-05' (country copy renders with or without child names)
 * - no match, or a child-relationship tier with no children resolved → 'info-circle',
 *   pairing with the generic bannerDefault text.
 */
export const deriveBannerIcon = (match: ContentFeedMatch | undefined, sourceType: SourceType, relatedCount: number): IconNames => {
  if (!match) {
    return 'info-circle';
  }

  // Country copy stays geographic even with no children resolved
  if (match.tier === 'country') {
    return 'globe-05';
  }

  // Remaining tiers fall back to bannerDefault when no children resolve
  if (relatedCount === 0) {
    return 'info-circle';
  }
  switch (match.tier) {
    case 'participant':
      {
        return relatedCount > 1 ? 'children' : 'child';
      }
    case 'fcp':
      {
        return sourceType === 'community' ? 'fcp' : 'marker-pin-04';
      }
    case 'cluster':
      {
        return 'signpost';
      }
    default:
      {
        return 'info-circle';
      }
  }
};
export const useComponentContentFeedCard = props => {
  const $ = _c(61);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    sourceType,
    icon,
    title,
    description,
    media: t1,
    fundingProgress,
    link,
    programCountryName,
    match,
    bannerText,
    hasNewUpdate: t2,
    ctaLabel,
    publishedAt,
    author,
    participantNameMap,
    entryParticipants
  } = props;
  const media = t1 === undefined ? emptyMedia : t1;
  const hasNewUpdate = t2 === undefined ? false : t2;
  const isCausesLayout = sourceType === "causes" || sourceType === "cause-updates" || !sourceType;
  const isPostLayout = sourceType === "community" || sourceType === "compassion.media";
  let authorLine;
  if (author) {
    authorLine = author.role ? `${author.displayName}, ${author.role}` : author.displayName;
  }
  let t3;
  if ($[1] !== publishedAt) {
    t3 = publishedAt ? getElapsed(publishedAt) : undefined;
    $[1] = publishedAt;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const elapsed = t3;
  const formattedPublishedAt = elapsed && typeof elapsed === "string" ? elapsed : undefined;
  let t4;
  if ($[3] !== media) {
    const imageMedia = media.filter(_temp);
    const videoMedia = media.filter(_temp2);
    t4 = [...imageMedia, ...videoMedia];
    $[3] = media;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const allMedia = t4;
  let t5;
  if ($[5] !== allMedia) {
    t5 = allMedia.slice(0, 3);
    $[5] = allMedia;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const galleryItems = t5;
  const allGalleryItems = allMedia;
  const totalPhotos = allMedia.length;
  let bannerI18n;
  let bannerTextOverride;
  let bannerTooltipNames;
  let t6;
  if ($[7] !== bannerText || $[8] !== entryParticipants || $[9] !== icon || $[10] !== match || $[11] !== participantNameMap || $[12] !== programCountryName || $[13] !== sourceType) {
    const relatedChildren = resolveRelatedChildren(match, entryParticipants, participantNameMap);
    bannerI18n = deriveBannerText(match, relatedChildren, programCountryName, sourceType);
    bannerTextOverride = bannerText;
    bannerTooltipNames = bannerI18n.tooltipNames?.length ? bannerI18n.tooltipNames : undefined;
    t6 = icon ?? deriveBannerIcon(match, sourceType, relatedChildren.count);
    $[7] = bannerText;
    $[8] = entryParticipants;
    $[9] = icon;
    $[10] = match;
    $[11] = participantNameMap;
    $[12] = programCountryName;
    $[13] = sourceType;
    $[14] = bannerI18n;
    $[15] = bannerTextOverride;
    $[16] = bannerTooltipNames;
    $[17] = t6;
  } else {
    bannerI18n = $[14];
    bannerTextOverride = $[15];
    bannerTooltipNames = $[16];
    t6 = $[17];
  }
  const resolvedIcon = t6;
  const progressPercent = Math.min(fundingProgress?.percentageFunded ?? 0, 100);
  let t7;
  if ($[18] !== fundingProgress || $[19] !== t) {
    t7 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.raised / 100
    }) : "";
    $[18] = fundingProgress;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const formattedRaised = t7;
  let t8;
  if ($[21] !== fundingProgress || $[22] !== t) {
    t8 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.goal / 100
    }) : "";
    $[21] = fundingProgress;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const formattedGoal = t8;
  const isFundedOrClosed = fundingProgress?.status === "funded" || fundingProgress?.status === "closed";
  let t9;
  if ($[24] !== fundingProgress || $[25] !== isFundedOrClosed || $[26] !== t) {
    t9 = isFundedOrClosed && fundingProgress?.fundedDate ? t("my:contentFeed.fundedOn", {
      date: fundingProgress.fundedDate
    }) : undefined;
    $[24] = fundingProgress;
    $[25] = isFundedOrClosed;
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const fundedMessage = t9;
  let t10;
  if ($[28] !== allMedia || $[29] !== title) {
    t10 = allMedia.map(m_1 => {
      if (m_1.type === "video") {
        return {
          media: {
            video: {
              url: m_1.url,
              thumbnailUrl: m_1.thumbnailUrl,
              width: m_1.width,
              height: m_1.height
            },
            image: m_1.thumbnailUrl ? {
              url: m_1.thumbnailUrl,
              alt: m_1.alt || title,
              width: m_1.width ?? fallbackImageWidth,
              height: m_1.height ?? fallbackImageHeight
            } : undefined
          }
        };
      }
      return {
        media: {
          image: {
            url: m_1.url,
            alt: m_1.alt || title,
            width: m_1.width ?? fallbackImageWidth,
            height: m_1.height ?? fallbackImageHeight
          }
        }
      };
    });
    $[28] = allMedia;
    $[29] = title;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const lightboxItems = t10;
  let t11;
  if ($[31] !== ctaLabel || $[32] !== t) {
    t11 = ctaLabel ?? t("my:contentFeed.donate");
    $[31] = ctaLabel;
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const resolvedCtaLabel = t11;
  let t12;
  if ($[34] !== allGalleryItems || $[35] !== author || $[36] !== authorLine || $[37] !== bannerI18n || $[38] !== bannerTextOverride || $[39] !== bannerTooltipNames || $[40] !== description || $[41] !== formattedGoal || $[42] !== formattedPublishedAt || $[43] !== formattedRaised || $[44] !== fundedMessage || $[45] !== fundingProgress || $[46] !== galleryItems || $[47] !== hasNewUpdate || $[48] !== isCausesLayout || $[49] !== isFundedOrClosed || $[50] !== isPostLayout || $[51] !== lightboxItems || $[52] !== link || $[53] !== programCountryName || $[54] !== progressPercent || $[55] !== resolvedCtaLabel || $[56] !== resolvedIcon || $[57] !== t || $[58] !== title || $[59] !== totalPhotos) {
    t12 = {
      isCausesLayout,
      isPostLayout,
      author,
      authorLine,
      formattedPublishedAt,
      description,
      icon: resolvedIcon,
      title,
      fundingProgress,
      link,
      programCountryName,
      hasNewUpdate,
      galleryItems,
      allGalleryItems,
      totalPhotos,
      resolvedBannerText: bannerTextOverride,
      bannerI18n,
      bannerTooltipNames,
      progressPercent,
      formattedRaised,
      formattedGoal,
      fundedMessage,
      isFundedOrClosed,
      lightboxItems,
      resolvedCtaLabel,
      t
    };
    $[34] = allGalleryItems;
    $[35] = author;
    $[36] = authorLine;
    $[37] = bannerI18n;
    $[38] = bannerTextOverride;
    $[39] = bannerTooltipNames;
    $[40] = description;
    $[41] = formattedGoal;
    $[42] = formattedPublishedAt;
    $[43] = formattedRaised;
    $[44] = fundedMessage;
    $[45] = fundingProgress;
    $[46] = galleryItems;
    $[47] = hasNewUpdate;
    $[48] = isCausesLayout;
    $[49] = isFundedOrClosed;
    $[50] = isPostLayout;
    $[51] = lightboxItems;
    $[52] = link;
    $[53] = programCountryName;
    $[54] = progressPercent;
    $[55] = resolvedCtaLabel;
    $[56] = resolvedIcon;
    $[57] = t;
    $[58] = title;
    $[59] = totalPhotos;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  return t12;
};
function _temp(m) {
  return m.type === "image";
}
function _temp2(m_0) {
  return m_0.type === "video";
}